import {LMColumn} from "../../components/Tables/types";

const FORMULA_START = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^[+-]?\d+([.,]\d+)?$/;

/**
 * Excel and LibreOffice run a cell that starts with =, +, -, @, tab or CR as a
 * formula, even inside quotes. The exported text is entered by users (customer
 * name, location, URL), so such a value gets a leading apostrophe and stays text.
 * A plain number such as "-5" is left as it is. Kept identical to the server's
 * ProductCsvExportService, whose monthly export mirrors this one.
 */
export function neutraliseFormula(value: unknown): string {
  const stringValue = String(value);
  if (typeof value === "string" && FORMULA_START.test(stringValue) && !PLAIN_NUMBER.test(stringValue)) {
    return `'${stringValue}`;
  }
  return stringValue;
}

export class DataGridCsvExporter {
  constructor(
    private readonly rows: Record<string, any>[],
    private readonly columns: LMColumn[],
    private readonly options?: {
      filename?: string;
      delimiter?: string;
      includeHeaders?: boolean;
    }
  ) {}

  public exportToCsv(): void {
    const csvString = this.buildCsvString();
    this.downloadCsv(csvString);
  }

  private buildCsvString(): string {
    const { delimiter = ",", includeHeaders = true } = this.options || {};

    const escapeValue = (value: any): string => {
      // A missing value is written as "" rather than nothing: a spreadsheet that merges
      // consecutive delimiters on import drops a bare empty field and shifts the row.
      // The server's ProductCsvExportService still writes nothing here.
      if (value == null) return '""';
      const stringValue = neutraliseFormula(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    };

    const columns = this.columns
    // The labels of the columns as displayed in the table
    const headerNames = columns.map(column => column.headerName ?? "")

    const headerRow = includeHeaders ? headerNames.map(escapeValue).join(delimiter) : "";

    const valueOf = (row: Record<string, any>, column: LMColumn) =>
      column.valueGetter ? column.valueGetter(undefined, row) : row[column.field];

    const dataRows = this.rows.map(row =>
      columns
        .map(column => escapeValue(valueOf(row, column)))
        .join(delimiter)
    );

    return [headerRow, ...dataRows].filter(Boolean).join("\n");
  }

  private downloadCsv(csv: string): void {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = this.options?.filename ?? "data_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }
}
